import { useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { EuiFlexGroup, EuiFlexItem, EuiSelectableOption, EuiSpacer, EuiText } from "@elastic/eui";
import TopFilter from "components/TopFilter/TopFilter";
import AlertsService from "services/AdminConsole/Monitoring/Profiles/MonitoringProfilesService";
import LookupService from "services/LookupService";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import ProfileResourcesList from "./ProfileResourcesList";
import {
  AvailableInstance,
  Resource,
  SelectedResource,
  CheckAssignmentResult,
} from "../../profileTypes";
import Modal from "components/Modal/Modal";
import Button from "components/Buttons/Button";
import { useNewProfileResourcesStyles } from "../../profileStyles";
import SelectablePopover, { SelectablePopoverOption } from "components/Popover/SelectablePopover";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";

interface Props {
  selectedResources: any[];
  setSelectedResources: React.Dispatch<React.SetStateAction<any[]>>;
  handleChangeSelectedResources: (resources: Resource[]) => void;
  checkPreviousAssignments: (selectedResources: SelectedResource[]) => CheckAssignmentResult;
  setIsDirty: (isDirty: boolean) => void;
  isSystemProfile?: boolean;
}

type CustomSelectableOption = EuiSelectableOption & {
  key: string;
  tags: { tagName: string; tagValue: string }[];
  monitoringProfileId?: number;
  monitoringProfileName?: string;
};

const ProfileResources = ({
  selectedResources,
  setSelectedResources,
  handleChangeSelectedResources,
  checkPreviousAssignments,
  setIsDirty,
  isSystemProfile,
}: Props) => {
  const classes = useNewProfileResourcesStyles();
  const dispatch = useDispatch();
  const { clientId, GlobalTopFilterObject, GlobalTopFilterObjectValues } = useSelector(
    globalFilterSelector,
    shallowEqual
  );

  const [availableResources, setAvailableResources] = useState<AvailableInstance[]>([]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [options, setOptions] = useState<CustomSelectableOption[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isResourcesWarningModalOpen, setIsResourcesWarningModalOpen] = useState(false);
  const [pendingSelections, setPendingSelections] = useState<CustomSelectableOption[]>([]);
  const [isLoadingResources, setIsLoadingResources] = useState(false);

  const [platformOptions, setPlatformOptions] = useState<any[]>([]);

  const [filteredPlatformOptions, setFilteredPlatformOptions] = useState<any[]>([]);

  useEffect(() => {
    if (GlobalTopFilterObject?.resourceType?.length > 0) {
      setFilteredPlatformOptions(
        platformOptions?.filter((o: any) =>
          GlobalTopFilterObject?.resourceType?.includes(o.resourceType)
        )
      );
    } else {
      setFilteredPlatformOptions(platformOptions);
    }
  }, [GlobalTopFilterObject?.resourceType]);

  const filters = useMemo(
    () => [
      {
        field: "resourceAssignedProfile",
        name: "Assigned Profile",
        options: [
          { name: "Assigned", value: "Assigned" },
          { name: "Not Assigned", value: "Not Assigned" },
        ],
        allowExclude: false,
      },
      {
        field: "resourceType",
        name: "Resource Type",
        options: [
          { name: "Host", value: "Host" },
          { name: "Instance", value: "Instance" },
        ],
        onclick: (v: any) => {
          dispatch({
            type: globalFilterTypes.UPDATE_TOP_FILTER,
            payload: {
              resourceType: v,
            },
          });
        },
        allowExclude: false,
      },
      {
        field: "platformId",
        name: "Platform",
        options: filteredPlatformOptions,
        allowExclude: false,
      },
    ],
    [clientId, filteredPlatformOptions]
  );

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);

    const searchInput = document.querySelector(".euiFieldSearch") as HTMLInputElement;
    const searchTerm = searchInput?.value?.toLowerCase() || "";

    const updatedOptions = options.map((option) => {
      if (searchTerm && !option.label.toLowerCase().includes(searchTerm)) {
        return option;
      }
      return {
        ...option,
        checked: isChecked ? ("on" as const) : undefined,
      };
    });

    setOptions(updatedOptions);
    setPendingSelections(updatedOptions.filter((opt) => opt.checked === "on"));
    setIsDirty(true);
  };

  const getAvailableResources = async () => {
    setIsLoadingResources(true);
    try {
      const result = await AlertsService.getAvailableResources({
        clientId,
      });
      if (result.resources) {
        setAvailableResources(result.resources);
      }
    } catch (error) {
    } finally {
      setIsLoadingResources(false);
    }
  };

  const onButtonClick = () => {
    setIsPopoverOpen(!isPopoverOpen);
  };

  const closePopover = () => {
    setIsPopoverOpen(false);
  };

  const filteredAvailableResources = useMemo(
    () =>
      availableResources
        .filter((i) => {
          const assignedProfiles = GlobalTopFilterObject?.resourceAssignedProfile || [];
          const hasAssigned = assignedProfiles.includes("Assigned");
          const hasNotAssigned = assignedProfiles.includes("Not Assigned");

          if (hasAssigned && hasNotAssigned) return true;
          if (hasAssigned) return i.monitoringProfileName !== null;
          if (hasNotAssigned) return i.monitoringProfileName === null;
          return true;
        })
        ?.filter((i: any) => {
          const assignedResourceTypes = GlobalTopFilterObject?.resourceType || [];

          if (assignedResourceTypes?.length > 0) {
            return assignedResourceTypes?.includes(i?.resourceType);
          }
          return true;
        })
        ?.filter((i: any) => {
          const assignedPlatformIds = GlobalTopFilterObjectValues?.platformId || [];

          if (assignedPlatformIds?.length > 0) {
            return assignedPlatformIds?.includes(`${i?.platformId}`);
          }
          return true;
        }),

    [availableResources, GlobalTopFilterObject, isPopoverOpen]
  );

  const handleApplyResources = () => {
    const checkedOptions = pendingSelections.filter((o) => o.checked === "on");
    const filteredOutSelected = selectedResources.filter(
      (selected) => !options.some((opt) => opt.key === selected.key)
    );

    const allSelectedOptions = [...checkedOptions, ...filteredOutSelected];

    const formattedSelections = allSelectedOptions.map((option) => ({
      key: option.key,
      label: option.label,
      resourceId: parseInt(option.key),
      resourceName: option.label,
      tags: option.tags || [],
      checked: undefined,
      monitoringProfileId: option.monitoringProfileId,
      monitoringProfileName: option.monitoringProfileName,
      resourceType: option.resourceType,
    }));

    setSelectedResources(formattedSelections);
    handleChangeSelectedResources(formattedSelections);
    closePopover();
  };

  useEffect(() => {
    getAvailableResources();
    LookupService.getPlatforms().then((result) => {
      const platforms = result?.data?.map((i: any) => ({
        ...i,
        text: i?.name,
        label: i?.name,
      }));
      setPlatformOptions(platforms);
      setFilteredPlatformOptions(platforms);
    });
  }, [clientId]);

  useEffect(() => {
    if (Array.isArray(filteredAvailableResources) && filteredAvailableResources.length > 0) {
      const newOptions: CustomSelectableOption[] = filteredAvailableResources.map((i) => ({
        ...i,
        label: i.resourceName,
        key: i.resourceId.toString(),
        checked: undefined,
        tags: i.tags,
        monitoringProfileId: i.monitoringProfileId,
        monitoringProfileName: i.monitoringProfileName,
      }));
      setOptions(newOptions);
    } else {
      setOptions([]);
    }
  }, [filteredAvailableResources]);

  // Update options and select all state
  useEffect(() => {
    // Always update selectAll state regardless of popover
    setSelectAll(options.length > 0 && options.length === selectedResources.length);

    // Update options only when popover opens
    if (isPopoverOpen) {
      const updatedOptions = options.map((option) => {
        const isInTable = selectedResources.some((selected) => selected.key === option.key);
        return {
          ...option,
          checked: isInTable ? ("on" as const) : undefined,
        };
      });
      setOptions(updatedOptions);
    }
  }, [isPopoverOpen, selectedResources, options.length]);

  const ReassignResourcesFooter = () => {
    return (
      <>
        <EuiFlexItem grow={false}>
          <Button
            type="secondary"
            text="No"
            onClick={() => {
              setIsPopoverOpen(true);
              setIsResourcesWarningModalOpen(false);
            }}
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <Button
            type="primary"
            text="Yes"
            onClick={() => {
              handleApplyResources();
              setIsResourcesWarningModalOpen(false);
            }}
          />
        </EuiFlexItem>
      </>
    );
  };

  const handleApplyClick = () => {
    const selectionsToCheck = pendingSelections.map((opt) => ({
      key: opt.key,
      label: opt.label,
      checked: "on" as const,
      monitoringProfileId: opt.monitoringProfileId || 0,
      monitoringProfileName: opt.monitoringProfileName || "",
      resourceId: parseInt(opt.key),
      resourceName: opt.label,
      tags: opt.tags || [],
    }));

    const { hasAssignedResources } = checkPreviousAssignments(selectionsToCheck);

    if (hasAssignedResources) {
      setIsPopoverOpen(false);
      setIsResourcesWarningModalOpen(true);
    } else {
      handleApplyResources();
    }
    setIsDirty(true);
  };

  const handleClearSelection = () => {
    const updatedResources = selectedResources.filter((i) => i.checked !== "on");
    setSelectedResources(updatedResources);
    handleChangeSelectedResources(updatedResources);
    setIsDirty(true);
  };

  const selectableOptions: SelectablePopoverOption[] = options.map((option) => ({
    ...option,
    secondaryValue: option.monitoringProfileName,
  }));

  const handleSelectableOptionsChange = (newOptions: SelectablePopoverOption[]) => {
    const updatedOptions = newOptions.map((newOpt) => {
      const existingOpt = options.find((opt) => opt.key === newOpt.key);
      return {
        ...newOpt,
        tags: existingOpt?.tags || [],
        monitoringProfileId: existingOpt?.monitoringProfileId,
        monitoringProfileName: existingOpt?.monitoringProfileName,
      } as CustomSelectableOption;
    });
    setOptions(updatedOptions);
    setPendingSelections(updatedOptions.filter((opt) => opt.checked === "on"));
    setIsDirty(true);

    // Update selectAll state based on filtered options if search is active
    const searchInput = document.querySelector(".euiFieldSearch") as HTMLInputElement;
    const searchTerm = searchInput?.value?.toLowerCase() || "";

    if (searchTerm) {
      const visibleOptions = updatedOptions.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm)
      );
      setSelectAll(visibleOptions.every((opt) => opt.checked === "on"));
    } else {
      setSelectAll(updatedOptions.every((opt) => opt.checked === "on"));
    }
  };

  return (
    <>
      {isResourcesWarningModalOpen && (
        <Modal
          title="Reassign resources"
          onClose={() => setIsResourcesWarningModalOpen(false)}
          footer={<ReassignResourcesFooter />}>
          <EuiText>
            <p>
              A resource can only be assigned to one profile at a time. Do you want to remove the
              selected resource from its current profile and assign it to this profile instead?
            </p>
          </EuiText>
        </Modal>
      )}
      <div>
        <TopFilter Filters={filters} />
        <div className={classes.controlContainer}>
          <EuiFlexGroup
            className={classes.fullWidth}
            alignItems="center"
            justifyContent="spaceBetween">
            <EuiFlexItem grow={false} style={{ width: "50%" }}>
              <SelectablePopover
                isOpen={isPopoverOpen}
                onClose={closePopover}
                onButtonClick={onButtonClick}
                onApply={handleApplyClick}
                options={selectableOptions}
                onOptionsChange={handleSelectableOptionsChange}
                selectAll={selectAll}
                onSelectAllChange={handleSelectAll}
                buttonText="Select Resource"
                searchPlaceholder="Search resources..."
                columnHeaders={["Resource Name", "Resource Type", "Assigned Profile"]}
                isLoading={isLoadingResources}
                disabled={isSystemProfile}
                className={classes.selectButton}
              />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <Button
                text="Bulk Delete"
                type="primary"
                disabled={selectedResources.every((selection) => selection.checked !== "on")}
                onClick={handleClearSelection}
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        </div>
        <EuiSpacer size="m" />
        <ProfileResourcesList
          selectedResources={selectedResources}
          changeSelectedResources={handleChangeSelectedResources}
          isSystemProfile={isSystemProfile}
        />
      </div>
    </>
  );
};

export default ProfileResources;
